import React from 'react';
import { MapPin, Phone, Mail, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

// Contact information - easy to update
const CONTACT_INFO = {
  address: "R. Portugal, 161 - Bom Jardim, São José do Rio Preto - SP, 15084-070",
  phone: "+55 11 99557-9395",
  phoneClean: "5511995579395", // For WhatsApp link
  email: "lucaspessoa.adv@gmail.com",
};

// Google Maps embed URL for the address
const GOOGLE_MAPS_EMBED = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3733.123456789!2d-49.3797!3d-20.8197!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94bdad6f1c8d1c3d%3A0x1234567890abcdef!2sR.%20Portugal%2C%20161%20-%20Bom%20Jardim%2C%20S%C3%A3o%20Jos%C3%A9%20do%20Rio%20Preto%20-%20SP%2C%2015084-070!5e0!3m2!1spt-BR!2sbr!4v1234567890";

export const LocationSection = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${CONTACT_INFO.phoneClean}?text=Olá! Vi o site de vocês e gostaria de mais informações sobre os serviços jurídicos.`, '_blank');
  };

  const handleEmail = () => {
    window.location.href = `mailto:${CONTACT_INFO.email}?subject=Contato via Site - Pessoa & Pessoa Advocacia`;
  };

  return (
    <section 
      className="relative w-full bg-primary py-16 lg:py-24 overflow-hidden"
      data-testid="location-section"
      id="contato"
    >
      {/* Container */}
      <div className="container mx-auto px-4 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-12 lg:mb-16">
          <span className="text-accent font-body text-sm font-medium tracking-[0.2em] uppercase mb-3 block">
            Venha nos Visitar
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary-foreground font-semibold mb-4">
            Localização e Contato
          </h2>
          <div className="mx-auto w-20 h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          
          {/* Left Column - Contact Information */}
          <div className="flex flex-col" data-testid="contact-info">
            
            {/* Intro Text */}
            <p className="text-primary-foreground/80 font-body text-base lg:text-lg leading-relaxed mb-8">
              Estamos prontos para atendê-lo de forma <strong className="text-primary-foreground">presencial</strong> em 
              nosso escritório ou <strong className="text-primary-foreground">online</strong>, com a mesma dedicação e 
              profissionalismo. Entre em contato e agende sua consulta.
            </p>
            
            {/* Contact Details */}
            <div className="space-y-6 mb-10">
              
              {/* Address */}
              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <span className="text-primary-foreground/60 font-body text-sm uppercase tracking-wider block mb-1">
                    Endereço
                  </span>
                  <p className="text-primary-foreground font-body text-base lg:text-lg font-medium">
                    {CONTACT_INFO.address}
                  </p>
                </div>
              </div>
              
              {/* Phone/WhatsApp */}
              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <span className="text-primary-foreground/60 font-body text-sm uppercase tracking-wider block mb-1">
                    Telefone / WhatsApp
                  </span>
                  <p className="text-primary-foreground font-body text-base lg:text-lg font-medium">
                    {CONTACT_INFO.phone}
                  </p>
                </div>
              </div>
              
              {/* Email */}
              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <span className="text-primary-foreground/60 font-body text-sm uppercase tracking-wider block mb-1">
                    E-mail
                  </span>
                  <p className="text-primary-foreground font-body text-base lg:text-lg font-medium">
                    {CONTACT_INFO.email}
                  </p>
                </div>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4" data-testid="contact-ctas">
              <Button 
                onClick={handleWhatsApp}
                className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-body font-medium px-8 py-6 rounded-full transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-3 text-base"
                data-testid="whatsapp-button"
              >
                <MessageCircle className="w-5 h-5" />
                Chamar no WhatsApp
              </Button>
              
              <Button 
                onClick={handleEmail}
                variant="outline"
                className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:border-primary-foreground/50 font-body font-medium px-8 py-6 rounded-full transition-all duration-300 flex items-center justify-center gap-3 text-base"
                data-testid="email-button"
              >
                <Mail className="w-5 h-5" />
                Enviar E-mail
              </Button>
            </div>
          </div>

          {/* Right Column - Google Maps */}
          <div className="relative" data-testid="map-container">
            <div className="relative overflow-hidden rounded-xl shadow-elegant bg-primary-foreground/5 border border-primary-foreground/10">
              {/* Map iframe */}
              <div className="aspect-[4/3] lg:aspect-square w-full">
                <iframe
                  src={GOOGLE_MAPS_EMBED}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização Pessoa & Pessoa Advocacia"
                  className="w-full h-full"
                  data-testid="google-map"
                />
              </div>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-accent/20 rounded-xl -z-10 hidden lg:block" />
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-accent/10 rounded-xl -z-10 hidden lg:block" />
          </div>
        </div>
      </div>
      
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-1/4 h-full opacity-5 pointer-events-none">
        <div className="absolute top-1/3 right-10 w-48 h-48 rounded-full bg-primary-foreground blur-3xl" />
        <div className="absolute bottom-1/3 right-20 w-32 h-32 rounded-full bg-accent blur-2xl" />
      </div>
    </section>
  );
};

export default LocationSection;
